import c from 'classnames';
import { isArray } from 'lodash/fp';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import Alert from '../../components/Alert';
import Button from '../../components/Button';
import Header from '../../components/Header';
import IconDashboard from '../../components/icons/IconDashboard';
import Loader from '../../components/Loader';
import { useNavigateTo } from '../../components/Sidebar';
import { CurrentPlanContext } from '../../hooks/CurrenPlanContext';
import { useUpgradeModal } from '../../hooks/useUpgradeModal';
import { ROUTES } from '../../routes/routes';
import { ChurnData, EventData, MrrData } from '../../types/dashboard';
import { StripePrice } from '../../types/stripePrice';
import { StripeSubscription } from '../../types/stripeSubscription';
import { formatCurrency, formatFrequency, formatPrice } from '../../utils/currency';
import { useAxios } from '../../utils/hooks';
import { isPaidPlan } from '../../utils/plans';
import { getDummyPrice } from '../../utils/products';
import ChurnChart from './ChurnChart';
import LineChart from './LineChart';
import ValueChart from './ValueChart';

type DummyData = Pick<StripeSubscription, 'id' | 'start_date' | 'status'> & {
    items: { data: { price: StripePrice }[] };
    plan: { product: { name: string } };
    dummy: boolean;
};

type StripeData = StripeSubscription | DummyData;

const getDummyData = (): StripeData[] =>
    Array(10)
        .fill('')
        .map((_, idx) => ({
            id: String(idx),
            start_date: new Date().getTime() / 1000,
            status: 'Active',
            items: { data: [{ price: getDummyPrice(9900) }] },
            plan: { product: { name: 'Your Product' } },
            dummy: true,
        }));

function isDummyData(data: StripeData): data is DummyData {
    return (data as DummyData).dummy !== undefined;
}

interface ResponseError {
    error: string;
}

// TODO: allow customers to switch between test and live mode
const isTestMode = false;

const Home = () => {
    const [eventData, setEventData] = useState<EventData[] | ResponseError>();
    const [subscriptions, setSubscriptions] = useState<StripeData[] | string>();
    const [mrr, setMrr] = useState<MrrData>();
    const [churnData, setChurnData] = useState<ChurnData[]>();
    const currentPlan = useContext(CurrentPlanContext);
    const navigateTo = useNavigateTo();
    const { state } = useLocation<{ upgrade: boolean; upgradePlan?: string } | undefined>();
    const [UpgradeModal, showUpgradePopup] = useUpgradeModal();

    const axios = useAxios();
    useEffect(() => {
        if (!axios) {
            return;
        }

        axios.current
            ?.get<EventData[]>('/pricing-pages/events')
            .then((res) => {
                setEventData(res.data);
            })
            .catch((err) => setEventData(err));

        axios.current
            ?.get<StripeSubscription[]>('/stripe/subscriptions?limit=10')
            .then((res) => {
                if (res.data.length > 0) {
                    setSubscriptions(res.data);
                } else {
                    setSubscriptions(getDummyData());
                }
            })
            .catch((err) => setSubscriptions(getDummyData()));
    }, [axios]);

    useEffect(() => {
        if (currentPlan && isPaidPlan(currentPlan)) {
            axios.current?.get<MrrData[]>(`/stripe/mrr?noMonths=1&mode=${isTestMode ? 'test' : 'live'}`).then((res) => {
                setMrr(res?.data?.[0]);
            });
            axios.current?.get<ChurnData[]>(`/churn?noMonths=1`).then((res) => {
                setChurnData(res?.data);
            });
        }
    }, [axios, currentPlan]);

    useEffect(() => {
        if (state?.upgrade) {
            showUpgradePopup(state.upgradePlan);
        }
    }, [state, showUpgradePopup]);

    return (
        <>
            <div className="w-full flex flex-col h-screen overflow-y-hidden">
                <div className="w-full overflow-x-hidden border-t flex flex-col">
                    <main className="w-full flex-grow p-4">
                        <Header>
                            <IconDashboard className="inline w-6 mr-3" /> Dashboard
                        </Header>
                        <div className="px-0 md:px-8">
                            <p className="text-xl pt-6 pb-3 flex items-center">Last 30 days</p>
                            <div className=" flex items-center justify-center">
                                <div className="w-full max-w-3xl">
                                    <div className="-mx-2 md:flex">
                                        {!eventData && <Loader full={false} />}
                                        {isError(eventData) && (
                                            <Alert title="Error" body="Something went wrong please try again" />
                                        )}
                                        {hasData(eventData) && (
                                            <>
                                                <LineChart
                                                    title="Page Loads"
                                                    data={eventData}
                                                    dataFilter={(data) => data.action === 'load'}
                                                    bgColor="rgba(69, 123, 157, 0.1)"
                                                    lineColor="rgba(69, 123, 157, 0.8)"
                                                />
                                                <LineChart
                                                    title="Checkouts"
                                                    data={eventData}
                                                    dataFilter={(data) => data.action === 'checkout'}
                                                    bgColor="rgba(230, 57, 70, 0.1)"
                                                    lineColor="rgba(230, 57, 70, 0.8)"
                                                />
                                                <LineChart
                                                    title="New Subscriptions"
                                                    data={eventData}
                                                    dataFilter={(data) => data.action === 'subscribe'}
                                                    bgColor="rgba(246, 153, 63, 0.1)"
                                                    lineColor="rgba(246, 153, 63, 0.8)"
                                                />
                                            </>
                                        )}
                                        {mrr && (
                                            <ValueChart
                                                title="MRR"
                                                value={formatCurrency(mrr.total, mrr.currency)}
                                                bgColor="rgba(246, 153, 63, 0.1)"
                                                lineColor="rgba(246, 153, 63, 0.8)"
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="w-full mt-12">
                                <p className="text-xl pb-3 flex items-center">Latest Subscriptions</p>
                                <div className="bg-white overflow-auto relative">
                                    {!subscriptions && <Loader full={false} />}
                                    {typeof subscriptions === 'string' && (
                                        <Alert title="Error" body="Something went wrong please try again" />
                                    )}
                                    {typeof subscriptions === 'object' && (
                                        <>
                                            {isDummyData(subscriptions[0]) && (
                                                <div className="absolute inset-0 bg-white bg-opacity-60 flex flex-col items-center justify-center">
                                                    <div className="font-bold mb-4">Accept your first payments</div>
                                                    <Button onClick={navigateTo(ROUTES.PAGES_NEW)} kind="tertiary">
                                                        Create a new Pricing Page
                                                    </Button>
                                                </div>
                                            )}
                                            <table className="min-w-full bg-white">
                                                <thead className="bg-blue-800 text-white">
                                                    <tr>
                                                        <th className="text-left py-3 px-4 uppercase font-semibold text-sm">
                                                            Date
                                                        </th>
                                                        <th className="text-left py-3 px-4 uppercase font-semibold text-sm">
                                                            Status
                                                        </th>
                                                        <th className="text-left py-3 px-4 uppercase font-semibold text-sm">
                                                            Plan
                                                        </th>
                                                        <th className="text-left py-3 px-4 uppercase font-semibold text-sm">
                                                            Amount
                                                        </th>
                                                        <th className="w-1/3 text-left py-3 px-4 uppercase font-semibold text-sm"></th>
                                                    </tr>
                                                </thead>
                                                <tbody
                                                    className={c({
                                                        'text-gray-700': !isDummyData(subscriptions[0]),
                                                        'text-gray-300 pointer-events-none': isDummyData(
                                                            subscriptions[0]
                                                        ),
                                                    })}
                                                >
                                                    {subscriptions.map(({ id, start_date, status, plan, items }) => (
                                                        <tr key={id}>
                                                            <td className="text-left py-3 px-4">
                                                                {new Date(start_date * 1000).toLocaleString('en-US', {
                                                                    day: '2-digit',
                                                                    month: 'long',
                                                                    year: 'numeric',
                                                                })}
                                                            </td>
                                                            <td className="text-left py-3 px-4">{status}</td>
                                                            <td className="text-left py-3 px-4">
                                                                {plan?.product?.name ?? 'Multiple Plans'}
                                                            </td>
                                                            <td className="text-left py-3 px-4">
                                                                {formatPrice(items.data[0].price) +
                                                                    formatFrequency(items.data[0].price)}
                                                            </td>
                                                            <td className="w-1/3 text-left py-3 px-4">
                                                                <a
                                                                    href={`https://dashboard.stripe.com${
                                                                        isTestMode ? '/test' : ''
                                                                    }/subscriptions/${id}`}
                                                                    className={c('link', {
                                                                        'text-gray-300 pointer-events-none':
                                                                            isDummyData(subscriptions[0]),
                                                                    })}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                >
                                                                    View in Stripe
                                                                </a>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </>
                                    )}
                                </div>
                            </div>
                            <ChurnChart data={churnData} currentPlan={currentPlan} />
                        </div>
                    </main>
                </div>
            </div>
            <UpgradeModal />
        </>
    );
};

function isError(eventData: EventData[] | ResponseError | undefined): eventData is ResponseError {
    return (eventData as ResponseError)?.error !== undefined;
}

function hasData(eventData: EventData[] | ResponseError | undefined): eventData is EventData[] {
    return isArray(eventData);
}

export default Home;
